import { useCallback, useEffect, useState } from 'react';
import { ElencoArticoli } from './features/articoli/ElencoArticoli';
import { ElencoPreventivi } from './features/preventivi/ElencoPreventivi';
import { ElencoCommesse } from './features/commesse/ElencoCommesse';
import { ElencoRichieste } from './features/acquisti/ElencoRichieste';
import { RigheDaOrdinare } from './features/acquisti/RigheDaOrdinare';
import { ElencoOrdini } from './features/acquisti/ElencoOrdini';
import { ElencoCarichi } from './features/acquisti/ElencoCarichi';
import { ElencoSubappalti } from './features/acquisti/ElencoSubappalti';
import { NuovoScontrino } from './features/scontrini/NuovoScontrino';
import { MieiIncassi } from './features/scontrini/MieiIncassi';
import { CassaNovara } from './features/scontrini/CassaNovara';
import { StoricoScontrini } from './features/scontrini/StoricoScontrini';
import { MieiScontrini } from './features/scontrini/MieiScontrini';
import { ElencoRapportini } from './features/rapportini/ElencoRapportini';
import { NuovoRapportino } from './features/rapportini/NuovoRapportino';
import { avviaSincronizzazioneAutomatica, coda } from './features/rapportini/coda';
import { ElencoUtenze } from './features/utenze/ElencoUtenze';
import { ElencoListini } from './features/listini/ElencoListini';
import { TestiStampa } from './features/stampa/TestiStampa';
import { PredefinitiPreventivo } from './features/preventivi/PredefinitiPreventivo';
import { ElencoRegistro } from './features/registro/ElencoRegistro';
import { BarraSessione } from './features/sessione/BarraSessione';
import { Campanella } from './features/sessione/Campanella';
import { Accesso } from './features/sessione/Accesso';
import { CambiaPassword } from './features/sessione/CambiaPassword';
import { FornitoreSessione } from './features/sessione/ContestoSessione';
import { Home } from './features/home/Home';
import type { ChiaveArea, Vista } from './lib/menu';
import {
  areaDiVista,
  areeVisibili,
  primaVistaDellArea,
  vistaDaAncora,
  vociDellArea,
  vociVisibili,
} from './lib/menu';
import type { Sessione } from './lib/sessione';
import { ANONIMO, sessioneCorrente } from './lib/sessione';
import { installaSelezioneEntrando } from './lib/selezioneEntrando';

type StatoSessione = 'attesa' | 'pronta' | 'assente' | 'irraggiungibile';

export function App() {
  const [vista, setVista] = useState<Vista>(() => vistaDaAncora(window.location.hash));
  const [inCoda, setInCoda] = useState(0);
  const [sessione, setSessione] = useState<Sessione>(ANONIMO);
  const [statoSessione, setStatoSessione] = useState<StatoSessione>('attesa');
  const [motivo, setMotivo] = useState<string | null>(null);
  const [cambioPassword, setCambioPassword] = useState(false);

  /*  Tre esiti e non due, perché sono tre cose diverse da dire.

      «assente» è il server che risponde e dice che non sa chi sei: è un problema di
      utenza. «irraggiungibile» è il server che non risponde affatto — l'API non ancora
      avviata, il che in sviluppo capita ogni volta che si parte dal frontend — ed è un
      problema di macchina. Con un solo messaggio per entrambi, un'API non ancora su
      manda a cercare un difetto nell'autenticazione: costato dieci minuti la prima volta
      che è capitato, e sarebbe costato di più a chi non ha scritto questo codice.  */
  const caricaSessione = useCallback(() => {
    setStatoSessione('attesa');
    setMotivo(null);

    void sessioneCorrente()
      .then(({ sessione: letta }) => {
        setSessione(letta);
        setStatoSessione(letta.autenticato ? 'pronta' : 'assente');
      })
      .catch((errore: Error) => {
        setMotivo(errore.message);
        setStatoSessione('irraggiungibile');
      });
  }, []);

  useEffect(caricaSessione, [caricaSessione]);

  useEffect(() => {
    void coda.quantiInAttesa().then(setInCoda);
    return avviaSincronizzazioneAutomatica(setInCoda);
  }, []);

  useEffect(() => {
    window.location.hash = `#${vista}`;
  }, [vista]);

  /*  Senza questo, l'ancora la scriviamo e non la leggiamo: il tasto «indietro» del
      browser cambiava l'indirizzo e lasciava la schermata dov'era, e un `#ordini`
      incollato in una finestra già aperta non apriva niente. Nessun ciclo con l'effetto
      qui sopra: quello scrive l'ancora della vista corrente, questo rilegge lo stesso
      valore, e impostare lo stesso stato non fa niente.  */
  useEffect(() => {
    const rileggi = () => setVista(vistaDaAncora(window.location.hash));
    window.addEventListener('hashchange', rileggi);
    return () => window.removeEventListener('hashchange', rileggi);
  }, []);

  /*  Entrando in una casella se ne seleziona il contenuto: chiesto in riunione il
      01/10/2026, ed è il gesto più ripetuto della compilazione. Installato qui, alla
      radice, e non casella per casella — il perché sta in `selezioneEntrando.ts`.  */
  useEffect(installaSelezioneEntrando, []);

  const visibili = vociVisibili(sessione);

  /*  Un'ancora incollata che punta a una schermata che non ci spetta: si torna alla
      pagina iniziale, dove ci sono le aree che ci spettano, invece di mostrare una
      schermata vuota che sembra un guasto. Non è una protezione — quella è del server —
      è non mettere l'utente davanti a un errore per un indirizzo che gli ha mandato un
      collega con un altro ruolo.  */
  const vistaAmmessa: Vista =
    vista === 'home' || visibili.some((v) => v.vista === vista) ? vista : 'home';

  /*  Il menu in alto è contestuale: dentro un'area mostra le sue voci e basta. Chiesto da
      Andrea il 20/09 insieme alla pagina iniziale, e sono la stessa cosa detta due volte
      — le macroaree servono a qualcosa solo se poi l'applicativo si comporta come se
      esistessero.  */
  const area: ChiaveArea | null = areaDiVista(vistaAmmessa);
  const vociDiQui = area === null ? [] : vociDellArea(area, sessione);

  /*  Le aree in cui si può davvero entrare: la tendina serve a spostarsi, e un'area in
      arrivo non è un posto dove andare. Con una sola area la tendina non compare — non
      c'è niente da scegliere, e un menu a tendina con una voce sola è un ornamento.  */
  const areeAperte = areeVisibili(sessione).filter(
    (a) => primaVistaDellArea(a.chiave, sessione) !== null,
  );

  if (statoSessione === 'irraggiungibile') {
    return (
      <div className="guscio">
        <main>
          <div className="vuoto">
            <h2>TeraWorks non risponde</h2>
            <p>{motivo}</p>
            <p>
              Non è un problema di utenza: l'API non ha risposto. In sviluppo di solito
              vuol dire che non è ancora partita — <code>dotnet run --project
              backend/src/TeraWorks.Api</code> — e che questa pagina è stata aperta prima.
            </p>
            <button type="button" className="primario" onClick={caricaSessione}>
              Riprova
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (statoSessione === 'attesa') {
    return (
      <div className="guscio">
        <main>
          <p className="attesa">Verifica dell'utenza…</p>
        </main>
      </div>
    );
  }

  /*  Un ruolo che non ha nessuno dei permessi elencati: succede a un'utenza creata e
      non ancora profilata, e senza questo messaggio l'applicativo sarebbe una barra
      vuota — indistinguibile da un guasto.  */
  if (visibili.length === 0 && statoSessione === 'pronta') {
    return (
      <div className="guscio">
        <main>
          <div className="vuoto">
            <h2>Nessuna schermata per il tuo ruolo</h2>
            <p>
              Sei collegato come {sessione.nomeCompleto} ({sessione.ruolo}), ma quel ruolo
              non ha permessi su nessuna delle schermate esistenti. Va sistemato il
              profilo dell'utenza.
            </p>
          </div>
        </main>
      </div>
    );
  }

  /*  Non collegati, e si entra con una password: la schermata d'accesso. È qui e non
      dentro una rotta perché TeraWorks non ha rotte — ha un'ancora — e soprattutto perché
      così non esiste nessuna strada per arrivare a una schermata senza essere passati di
      qui.  */
  if (statoSessione === 'assente' && sessione.servePassword) {
    return (
      <div className="guscio">
        <main>
          <Accesso onEntrato={caricaSessione} postaCollegata={sessione.postaCollegata} />
        </main>
      </div>
    );
  }

  if (statoSessione === 'assente') {
    return (
      <div className="guscio">
        <main>
          <div className="vuoto">
            <h2>Non risulti collegato</h2>
            <p>
              TeraWorks non sa chi sei, quindi non può mostrarti niente. In sviluppo si
              dichiara l'utenza con l'intestazione <code>X-Utente</code>, oppure si
              imposta <code>Autenticazione:UtentePredefinito</code> nella configurazione
              dell'API.
            </p>
          </div>
        </main>
      </div>
    );
  }

  /*  Password provvisoria: questa schermata sta davanti a tutto il resto, e non c'è un
      modo di saltarla — chi è entrato con una password che conosce anche chi gliel'ha
      data non deve poter lavorare prima di averne una sua. Il flag arriva dal server a
      ogni richiesta, quindi appena è cambiata l'applicativo si apre da solo.  */
  if (sessione.deveCambiarePassword) {
    return (
      <div className="guscio">
        <main>
          <CambiaPassword obbligato onFatto={caricaSessione} />
        </main>
      </div>
    );
  }

  if (cambioPassword) {
    return (
      <div className="guscio">
        <main>
          <CambiaPassword
            obbligato={false}
            onFatto={() => setCambioPassword(false)}
            onAnnulla={() => setCambioPassword(false)}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="guscio">
      <nav className="navigazione">
        <span className="marchio">TeraWorks</span>

        {/*  La voce che riporta alla pagina iniziale. Sulla home non c'è: sarebbe un
             bottone che porta dove si è già.  */}
        {vistaAmmessa !== 'home' && (
          <button type="button" className="casa" onClick={() => setVista('home')}>
            Home
          </button>
        )}

        {vistaAmmessa !== 'home' && areeAperte.length > 1 && (
          <select
            className="area"
            aria-label="Macroarea"
            value={area ?? ''}
            onChange={(e) => {
              const dove = primaVistaDellArea(e.target.value as ChiaveArea, sessione);
              if (dove !== null) setVista(dove);
            }}
          >
            {areeAperte.map((a) => (
              <option key={a.chiave} value={a.chiave}>
                {a.etichetta}
              </option>
            ))}
          </select>
        )}

        {vociDiQui.map((voce) => (
          <button
            key={voce.vista}
            type="button"
            className={vistaAmmessa === voce.vista ? 'on' : ''}
            onClick={() => setVista(voce.vista)}
          >
            {voce.etichetta}
            {/* Il numero in coda va mostrato sempre: l'operaio deve sapere
                che qualcosa non è ancora arrivato in ufficio. */}
            {voce.vista === 'rapportino' && inCoda > 0 && <em className="coda">{inCoda}</em>}
          </button>
        ))}
        {/*  Gli avvisi (16/10/2026), accanto a chi e' collegato: sono suoi.  */}
        {sessione.autenticato && <Campanella />}
        <BarraSessione
          sessione={sessione}
          onCambiaPassword={() => setCambioPassword(true)}
          onUscito={caricaSessione}
        />
      </nav>

      <main>
        <FornitoreSessione sessione={sessione}>
        {vistaAmmessa === 'home' && (
          <Home sessione={sessione} inCoda={inCoda} onApri={setVista} />
        )}
        {vistaAmmessa === 'preventivi' && <ElencoPreventivi />}
        {vistaAmmessa === 'commesse' && <ElencoCommesse />}
        {vistaAmmessa === 'articoli' && <ElencoArticoli />}
        {vistaAmmessa === 'richieste' && <ElencoRichieste />}
        {vistaAmmessa === 'dordinare' && <RigheDaOrdinare />}
        {vistaAmmessa === 'ordini' && <ElencoOrdini />}
        {vistaAmmessa === 'carichi' && <ElencoCarichi />}
        {vistaAmmessa === 'subappalti' && <ElencoSubappalti />}
        {vistaAmmessa === 'scontrino' && <NuovoScontrino onIncassi={() => setVista('incassi')} />}
        {vistaAmmessa === 'incassi' && <MieiIncassi />}
        {vistaAmmessa === 'cassa' && <CassaNovara />}
        {vistaAmmessa === 'storico' && <StoricoScontrini />}
        {vistaAmmessa === 'scontrinimiei' && <MieiScontrini />}
        {vistaAmmessa === 'rapportini' && <ElencoRapportini />}
        {vistaAmmessa === 'listini' && <ElencoListini />}
        {vistaAmmessa === 'testi' && <TestiStampa />}
        {vistaAmmessa === 'predefiniti' && <PredefinitiPreventivo />}
        {vistaAmmessa === 'utenze' && <ElencoUtenze />}
        {vistaAmmessa === 'registro' && <ElencoRegistro />}
        {vistaAmmessa === 'rapportino' && sessione.id !== null && (
          <NuovoRapportino operaioId={sessione.id} onSalvato={setInCoda} />
        )}
        </FornitoreSessione>
      </main>
    </div>
  );
}
